import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { DomSanitizer, SafeResourceUrl, SafeUrl } from "@angular/platform-browser";

import {
  FileSheetGrid,
  FileUploadService,
} from "@bitwarden/web-vault/app/services/DataService/file-upload/file-upload.service";
import { MappedStatementRow } from "@bitwarden/web-vault/app/shared/utils/file-upload/statement-mapping.util";

/** Max rows rendered in the preview table. Preview is display-only — import reads the full grid. */
const PREVIEW_ROW_CAP = 200;

@Component({
  selector: "app-file-preview",
  templateUrl: "./file-preview.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      .preview-grid-scroll {
        height: 100%;
        max-width: 100%;
        overflow: auto;
        overscroll-behavior: contain;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
      }
      table.preview-grid {
        border-collapse: collapse;
        font-size: 12px;
        white-space: nowrap;
      }
      table.preview-grid th,
      table.preview-grid td {
        border: 1px solid #e2e8f0;
        padding: 4px 8px;
        text-align: left;
      }
      table.preview-grid thead th {
        position: sticky;
        top: 0;
        z-index: 2;
        background: #f1f5f9;
        font-weight: 600;
      }
      table.preview-grid .row-index {
        position: sticky;
        left: 0;
        z-index: 1;
        background: #f8fafc;
        color: #64748b;
        text-align: right;
      }
      table.preview-grid thead th.row-index {
        z-index: 3;
      }
      table.preview-grid tbody tr:nth-child(even) td {
        background: #fbfdff;
      }
      .sheet-tabs {
        display: flex;
        flex-wrap: wrap;
        gap: 2px;
        margin-top: 4px;
        border-top: 1px solid #cbd5e1;
        padding-top: 4px;
      }
      .sheet-tab {
        font-size: 12px;
        padding: 4px 12px;
        border: 1px solid #cbd5e1;
        border-bottom: none;
        border-radius: 4px 4px 0 0;
        background: #f1f5f9;
        color: #475569;
        cursor: pointer;
        white-space: nowrap;
      }
      .sheet-tab.active {
        background: #ffffff;
        color: #0f172a;
        font-weight: 600;
      }
    `,
  ],
})
export class FilePreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: { file: File };
  @Input() hasContainer = false;
  /**
   * Mapped statement rows. When set (after the column-mapping dialog confirms),
   * the preview shows this mapped table instead of the raw spreadsheet grid.
   */
  @Input() mappedRows: MappedStatementRow[] = [];
  private deviceDetectorService: DeviceDetectorService = inject(DeviceDetectorService);
  private sanitizer: DomSanitizer = inject(DomSanitizer);
  private fileUploadService: FileUploadService = inject(FileUploadService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private isSupportedFile = false;

  isAndroidMobile = false;
  //currently only support pdf and text preview, text is controlled by startWith('text/')
  // can add more types here in the future
  supportedFileTypes: string[] = [
    "application/pdf",
    // 'text/csv',
    // 'application/msword',
    // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    // 'application/vnd.ms-excel',
    // 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];

  /** Spreadsheet-style preview for csv / xls / xlsx files. */
  isTabular = false;
  sheets: FileSheetGrid[] = [];
  activeSheetIndex = 0;
  previewError = false;

  readonly previewRowCap = PREVIEW_ROW_CAP;

  /** Stable identity for *ngFor over preview rows — rows never reorder, so index is safe. */
  trackByIndex = (index: number): number => index;

  /** Cached safe URL for the non-tabular (PDF) iframe; revoked on change/destroy to avoid leaks. */
  pdfUrl: SafeResourceUrl | null = null;
  private pdfObjectUrl: string | null = null;
  /** Cached safe URL for the non-tabular (image) preview; revoked on change/destroy to avoid leaks. */
  imageUrl: SafeUrl | null = null;
  private imageObjectUrl: string | null = null;
  /** The File last parsed/url-built, so a new `data` wrapper around the same File is a no-op. */
  private lastLoadedFile: File | null = null;

  get activeSheet(): FileSheetGrid | null {
    return this.sheets[this.activeSheetIndex] ?? null;
  }

  /** Active sheet rows capped for rendering (full grid stays in memory for import). */
  get visibleSheetRows(): string[][] {
    return (this.activeSheet?.grid.rows ?? []).slice(0, PREVIEW_ROW_CAP);
  }

  /** Total rows in the active sheet (before the render cap). */
  get totalSheetRows(): number {
    return this.activeSheet?.grid.rows.length ?? 0;
  }

  /** Mapped rows capped for rendering. */
  get visibleMappedRows(): MappedStatementRow[] {
    return (this.mappedRows ?? []).slice(0, PREVIEW_ROW_CAP);
  }

  get totalMappedRows(): number {
    return this.mappedRows?.length ?? 0;
  }

  /** True when the preview table is showing fewer rows than the file actually holds. */
  get sheetTruncated(): boolean {
    return this.totalSheetRows > PREVIEW_ROW_CAP;
  }

  get mappedTruncated(): boolean {
    return this.totalMappedRows > PREVIEW_ROW_CAP;
  }

  /** True once a statement has been mapped — switches the preview to the mapped table. */
  get hasMappedRows(): boolean {
    return (this.mappedRows?.length ?? 0) > 0;
  }

  /** Only show the category column when at least one mapped row has a category. */
  get showMappedCategory(): boolean {
    return (this.mappedRows ?? []).some((row) => !!row.category);
  }

  selectSheet(index: number): void {
    this.activeSheetIndex = index;
  }

  ngOnInit() {
    // Android mobile devices check
    // Chrome for desktop views PDF through the Chrome PDF viewer plugin, while in mobile version the plugin is not supported
    // Android chrome currently redirects to google drive pdf viewer by default which requires an active url link accessible to the public. or users needs install the google pdf viewer app.
    // p.s. IOS chrome is just safari, different engine, and it supports pdf preview.
    // to be researched: chrome canery on android mobile seems to support pdf preview
    this.isAndroidMobile =
      this.deviceDetectorService.isMobile() && this.deviceDetectorService.os === "Android";
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The host binds `[data]="{ file }"` — a fresh object literal every change-detection
    // pass — so `changes.data` fires constantly. Only the underlying File matters, so parse
    // and build the URL once per distinct File, not per CD pass.
    if (changes.data) {
      const file = this.data?.file ?? null;
      if (file !== this.lastLoadedFile) {
        this.lastLoadedFile = file;
        this.loadTabularPreview();
        this.buildPdfUrl();
        this.buildImageUrl();
      }
    }
  }

  ngOnDestroy(): void {
    this.revokePdfUrl();
    this.revokeImageUrl();
  }

  isImageFile(fileType: string): boolean {
    return fileType.startsWith("image/");
  }

  /** Build the iframe URL once per file (not per change-detection pass) and revoke the previous one. */
  private buildPdfUrl(): void {
    this.revokePdfUrl();
    const file = this.data?.file;
    if (!file || !file.type || !this.supportedFileTypes.includes(file.type)) {
      this.pdfUrl = null;
      return;
    }
    this.pdfObjectUrl = URL.createObjectURL(file);
    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfObjectUrl);
  }

  private revokePdfUrl(): void {
    if (this.pdfObjectUrl) {
      URL.revokeObjectURL(this.pdfObjectUrl);
      this.pdfObjectUrl = null;
    }
    this.pdfUrl = null;
  }

  /** Build the image preview URL once per file (not per change-detection pass) and revoke the previous one. */
  private buildImageUrl(): void {
    this.revokeImageUrl();
    const file = this.data?.file;
    if (!file || !file.type || !this.isImageFile(file.type)) {
      this.imageUrl = null;
      return;
    }
    this.imageObjectUrl = URL.createObjectURL(file);
    this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(this.imageObjectUrl);
  }

  private revokeImageUrl(): void {
    if (this.imageObjectUrl) {
      URL.revokeObjectURL(this.imageObjectUrl);
      this.imageObjectUrl = null;
    }
    this.imageUrl = null;
  }

  /** When the file is tabular (csv/xls/xlsx), parse every worksheet into a grid for the table preview. */
  private loadTabularPreview(): void {
    const file = this.data?.file;
    this.isTabular = !!file && this.fileUploadService.isTabular(file);
    this.sheets = [];
    this.activeSheetIndex = 0;
    this.previewError = false;
    if (!this.isTabular) {
      return;
    }
    this.fileUploadService
      .getSheetGrids(file)
      .then((sheets) => {
        this.sheets = sheets;
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.previewError = true;
        this.cdr.markForCheck();
      });
  }

  isSupportedFileType(fileType: string): boolean {
    this.isSupportedFile = this.supportedFileTypes.includes(fileType);
    return this.isSupportedFile;
  }
}
